import UserDataStore from 'iam/UserDataStore'
import { task } from 'mobx-task'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { GetCurrentMember } from 'iam/SessionStore'
import { action, computed, makeObservable } from 'mobx'
import {
  StaticColumnDetails,
  MutableColumnDetails,
  ColumnId,
} from 'jobs/infra/JobsTableColumns'
import { CSVData, csvDownload } from 'utils/csv-util'
import SearchableJob from 'jobs/SearchableJob'
import { JobsExportAdapter } from 'jobs/infra/JobsExportAdapter'
import JobSearchViewModel, {
  TicketsAdapter,
} from 'jobs/infra/JobSearchViewModel'
import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import { FetchUserTeamMembers, FilterTeamMembers } from 'iam/TeamMemberStore'
import { FindUserTeams } from 'iam/TeamStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { JobsLoadFilterItemsAdapter } from 'jobs/infra/JobsLoadFilterItemsAdapter'
import { JobTicketStore } from 'jobs/tickets/JobTicketStore'
import { FindLegends } from 'legends/PublishedLegendStore'
import { Subscription, filter, identity } from 'rxjs'
import { first } from 'lodash'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { matchesWaitingOnFilter } from 'jobs/views/JobManagement/JobWaitingOnFilterOption'
import { TableSortOrder } from 'components/Table'
import link from 'infra/link'
import { Id } from 'types/definitions'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import Workspace from 'iam/models/Workspace'
import { JobStatusFilterOption } from 'jobs/views/JobManagement/JobStatusFilterOption'
import FirmDashboardContractViewModel from '../FirmDashboardContractViewModel'
import { SearchInventoryItems } from 'admin/InventoryStore'

/**
 * View model for managing the job's table within the firm dashboard.
 */
export default class FirmDashboardJobsViewModel {
  /**
   * Adapter to load filter items.
   */
  loadFilterItemsAdapter = new JobsLoadFilterItemsAdapter(
    this.getCurrentUserWorkspaceMemberships,
    this.findLegends,
    this.findUserTeams,
    this.searchServiceItems,
  )

  /**
   * Adapter for retrieving tickets.
   */
  ticketsAdapter: TicketsAdapter = {
    fetchLatestOpenTicketsForJobs:
      this.jobTicketStore.fetchLatestOpenTicketsForJobs,
  }

  /**
   * Job Searcher.
   */
  searcher = new JobSearchViewModel(
    this.jobFetcher,
    this.loadFilterItemsAdapter,
    this.ticketsAdapter,
    this.newAlert,
    identity,
    this.getCurrentMember,
    this.userDataStore,
    this.getCurrentUserWorkspaceMemberships,
    this.filterTeamMembers,
    this.createFilterStorage('FirmDashboardJobsTable'),
  )

  /**
   * Column manager.
   */
  columnManager = new FirmDashboardColumnManager(
    this.newAlert,
    this.searcher.getAllFilteredJobs,
  )

  contractInfo = new FirmDashboardContractViewModel()

  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Constructor
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private newAlert: AlertFactory,
    private findLegends: FindLegends,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private fetchUserTeamMembers: FetchUserTeamMembers,
    private getCurrentMember: GetCurrentMember,
    private jobTicketStore: JobTicketStore,
    private createFilterStorage: FilterStorageFactory,
    private userDataStore: UserDataStore,
    private filterTeamMembers: FilterTeamMembers,
    private _canEditDraftJobAsAdmin: () => boolean,
    private findUserTeams: FindUserTeams,
    private _useEditDraftJobForFirmAdmin: () => boolean,
    private searchServiceItems: SearchInventoryItems,
  ) {
    makeObservable(this)
  }

  /**
   * Whether the user can edit draft job as admin.
   */
  @computed
  get canEditDraftJobAsAdmin() {
    return this._canEditDraftJobAsAdmin()
  }

  /**
   * Whether the current member is a firm admin
   */
  @computed
  get useEditDraftJobForFirmAdmin() {
    return this._useEditDraftJobForFirmAdmin
  }

  /**
   * Only display jobs where the current user is a collaborator
   * and match the filters.
   */
  @computed
  get filteredJobs() {
    return this.searcher.jobs.filter((job) =>
      matchesWaitingOnFilter(
        job.waitingOn,
        first(this.searcher.filters.appliedFilters?.waitingOn),
      ),
    )
  }

  /**
   * Gets the workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.searcher.filters.getFilter('workspaces') as SelectFilter<
      string,
      Id
    >
  }

  /**
   * Gets the job status filter.
   */
  @computed
  get statusFilter() {
    return this.searcher.filters.getFilter('jobStatus') as SelectFilter<
      string,
      JobStatusFilterOption
    >
  }

  /**
   * Whether we are showing the empty job view
   */
  @computed
  get showEmptyView() {
    return (
      this.filteredJobs.length === 0 &&
      !this.searcher.scroller.fetch.pending &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * Field to sort by.
   */
  @computed
  get sortField() {
    return this.searcher.sorting.type === 'Field'
      ? this.searcher.sorting.field
      : ''
  }

  /**
   * Filter by current user workspace memberships
   * and returns a workspace.
   */
  @computed
  get currentWorkspace() {
    return (
      this.getCurrentUserWorkspaceMemberships().find(
        (ws) => ws.workspace.id === this.workspaceFilter.value[0],
      )?.workspace ?? this.getCurrentMember().workspace
    )
  }

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   * @returns
   */
  getSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
  ): TableSortOrder {
    return this.searcher.sorting.type === 'Field' &&
      this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   * @returns
   */
  setSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  getJobLink(job: SearchableJob) {
    return link.job(Id.assert(job.id))
  }

  /**
   * Gets the latest open ticket for the specified job.
   */
  getLatestOpenTicketForJob(job: SearchableJob) {
    return this.jobTicketStore.getLatestTicketForJob(job.id)
  }

  /**
   * Sets the workspace filter
   */
  setWorkspace(workspace: Workspace) {
    this.workspaceFilter.change([workspace.id])
    this.contractInfo.setCurrentWorkspace(workspace)
    this.searcher.setForcedCreatedDateRange({
      from: this.contractInfo.contractStartDate,
      to: this.contractInfo.contractEndDate,
    })
  }

  /**
   * Prepares the subscriptions.
   */
  private prepareSubscriptions() {
    if (this.canEditDraftJobAsAdmin) {
      this.subscription.add(
        // When a job is created, add it to the top of the list.
        // This is a workaround for the fact that the job searcher is not
        // processing the JobCreate event when filters are applied.
        this.jobFetcher.events$
          .pipe(filter((event) => event.type === 'JobCreated'))
          .subscribe((event) => {
            // We don't want to add the job to the list if it's already there or
            // if the user is searching for something or
            // if the job's workspace is not the one selected in the workspace filter.
            if (
              event.job.workspace.id !== this.currentWorkspace.id ||
              this.searcher.scroller.items.includes(event.job) ||
              this.searcher.searchText.value !== ''
            ) {
              return
            }
            action(() => this.searcher.scroller.items.unshift(event.job))
          }),
      )
    }
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    await this.fetchUserTeamMembers()

    this.prepareSubscriptions()

    this.searcher.filters.makeFilterImplicit('jobStatus')

    this.searcher.filters.removeFilter('jobType')
    this.searcher.filters.removeFilter('jobScopeUpdates')
    this.searcher.filters.removeFilter('waitingOn')
    this.searcher.filters.removeFilter('claimedRange')
    this.searcher.filters.removeFilter('closedRange')
    this.searcher.filters.removeFilter('deadlineRange')
    this.searcher.filters.removeFilter('providerCutAmountRange')
    this.searcher.filters.removeFilter('createdRange')
    this.searcher.filters.removeFilter('serviceItems')

    this.searcher.setForcedCreatedDateRange({
      from: this.contractInfo.contractStartDate,
      to: this.contractInfo.contractEndDate,
    })

    this.searcher.activate()
    this.setWorkspace(this.getCurrentMember().workspace)
  })

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.searcher.deactivate()
    this.subscription.unsubscribe()
  }
}

/**
 * Firm dashboard column manager.
 */
class FirmDashboardColumnManager {
  /**
   * Jobs Export Adapter.
   */
  private adapter = JobsExportAdapter(
    () => this.getAllFilteredJobs(),
    () => this.filteredColumns,
  )

  /**
   * Constructor
   */
  constructor(
    private newAlert: AlertFactory,
    private getAllFilteredJobs: () => Promise<SearchableJob[]>,
  ) {}

  /**
   * Triggers download of jobs data organized into a CSV file.
   */
  exportJobsAsCSV = async () => {
    const progress = this.newAlert().saying('Job exporting in progress').show()
    const csvData: CSVData = await this.adapter.jobsExportData()

    progress
      .withState(AlertState.Success)
      .saying(
        'Job export completed!',
        'The exported file should begin downloading...',
      )
      .autoDismiss()
    csvDownload(csvData, 'jobs.csv')
  }

  /**
   * Returns columns filtered by appropriate column types.
   */
  @computed
  get filteredColumns(): (StaticColumnDetails & MutableColumnDetails)[] {
    return [
      {
        id: ColumnId.JobTitle,
        header: 'Job title',
        modifyVisibility: false,
        field: 'Title',
        sortable: true,
        visible: true,
      },
      {
        id: ColumnId.JobShortId,
        header: 'ID',
        modifyVisibility: true,
        sortable: false,
        visible: true,
      },
      {
        id: ColumnId.Submitted,
        header: 'Date submitted',
        modifyVisibility: true,
        field: 'DateSubmitted',
        sortable: true,
        visible: true,
      },
      {
        id: ColumnId.ClientTeam,
        header: 'Client team',
        modifyVisibility: true,
        field: 'ClientTeamName',
        sortable: true,
        visible: true,
      },
      {
        id: ColumnId.ProgressionStep,
        header: 'Progression',
        modifyVisibility: true,
        sortable: false,
        visible: true,
      },
      {
        id: ColumnId.Status,
        header: 'Status',
        modifyVisibility: true,
        sortable: false,
        visible: true,
      },
      {
        id: ColumnId.Action,
        header: '',
        modifyVisibility: false,
        sortable: false,
        visible: true,
      },
    ]
  }
}
